import { useState, useCallback, useRef } from 'react'
import { fetchYouTubeCaption } from '@/services/youtube.service'
import { decodeHtmlEntities } from '@/utils/textUtils'
import type { YouTubeCaptionsResponse } from '@/types/youtubeCaptions.types'

/**
 * Caption fetch lifecycle state for a single segment.
 *
 * - idle: No fetch has been triggered yet (initial state or segment changed).
 * - loading: Fetch in progress.
 * - success: Fetch completed; captionAvailable and transcript reflect the result.
 *   Story 3.2: success also carries `transcriptId` and `source` for edit-mode decisions.
 * - no-captions: Video has no caption tracks — a valid expected state, not an error.
 * - error: Upstream failure (quota exceeded, API unavailable, network error).
 */
export type CaptionFetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'success'
      transcript: string
      /** DB row ID of the displayed transcript row (youtube or user). */
      transcriptId?: number
      /** 'youtube' or 'user' indicating priority display rule result. */
      source?: 'youtube' | 'user'
      /**
       * Option B: the YouTube cache transcript, present only when source='user' and a
       * youtube row also exists. Enables the panel to show a comparison view.
       */
      youtubeTranscript?: string
      /**
       * Furigana-annotated ruby HTML of the YouTube transcript (Japanese projects only).
       * Always represents the YouTube text — use this for display in place of the plain
       * youtube text when present.
       */
      altTranscript?: string
    }
  | { status: 'no-captions' }
  | { status: 'quota-exceeded' }
  | { status: 'api-unavailable' }
  | { status: 'error'; message: string }

/**
 * Caption text is stored HTML-escaped in the DB (YouTube tracks arrive with
 * &#39;, &quot;, …). Decode once at the response boundary so every consumer —
 * display, comparison column, and edit-mode seed — gets plain text. Decoding
 * preserves the ruby markup in alt_transcript (only character references are
 * converted, tags pass through as-is).
 */
const toSuccessState = (response: YouTubeCaptionsResponse): CaptionFetchState => ({
  status: 'success',
  transcript: decodeHtmlEntities(response.transcript ?? ''),
  transcriptId: response.transcript_id,
  source: response.source,
  youtubeTranscript:
    response.youtube_transcript != null
      ? decodeHtmlEntities(response.youtube_transcript)
      : undefined,
  altTranscript:
    response.alt_transcript != null ? decodeHtmlEntities(response.alt_transcript) : undefined,
})

/**
 * useYouTubeCaptions — manages user-initiated caption fetch for a YouTube segment.
 *
 * This hook is intentionally NOT a TanStack Query managed query because caption
 * fetch is strictly user-initiated from the "Get Transcript" button and must
 * never auto-fire on mount, route load, or segment change (AC 5 / story anti-patterns).
 *
 * Benefits of manual state over useQuery here:
 *  - No risk of background refetch or query key auto-trigger.
 *  - Explicit reset when the selected segment changes.
 *  - Clear mapping from service response shapes to distinct UI states.
 *
 * Usage:
 *   const { state, fetchCaption, reset } = useYouTubeCaptions()
 *   <Button onClick={() => fetchCaption(segment.id)} />
 *
 * When the selected segment changes, call reset() so the panel returns to idle.
 */
export const useYouTubeCaptions = () => {
  const [state, setState] = useState<CaptionFetchState>({ status: 'idle' })

  /**
   * Monotonically increasing counter used to discard stale async responses.
   * Same pattern as useYouTubeTranslations — prevents rapid segment-switching
   * from causing a prior segment's cache response to clobber the new segment.
   */
  const requestCounterRef = useRef(0)

  /**
   * Warm pre-fetch cache: segmentId → resolved CaptionFetchState.
   * Populated by prefetch(); read by fetchFromCache() to skip the loading flash
   * when the result is already known.
   */
  const prefetchCacheRef = useRef<Map<number, CaptionFetchState>>(new Map())

  /**
   * Fire-and-forget pre-fetch for a segment. Stores the resolved state in
   * prefetchCacheRef without touching the display state. Safe to call for
   * adjacent segments during playback.
   */
  const prefetch = useCallback(async (segmentId: number) => {
    // Already cached or in-flight — skip
    if (prefetchCacheRef.current.has(segmentId)) return
    // Mark in-flight with a sentinel so concurrent callers skip
    prefetchCacheRef.current.set(segmentId, { status: 'loading' })

    try {
      const response: YouTubeCaptionsResponse = await fetchYouTubeCaption({
        segment_id: segmentId,
        cache_only: true,
      })

      if (response.status === 'failed' || !response.caption_available) {
        prefetchCacheRef.current.delete(segmentId)
        return
      }

      prefetchCacheRef.current.set(segmentId, toSuccessState(response))
    } catch {
      prefetchCacheRef.current.delete(segmentId)
    }
  }, [])

  /**
   * Trigger a full caption fetch for the given segment ID (including yt-dlp if needed).
   * Only { segment_id } is sent — timestamps are resolved on the backend.
   */
  const fetchCaption = useCallback(async (segmentId: number) => {
    const requestId = ++requestCounterRef.current
    setState({ status: 'loading' })

    try {
      const response: YouTubeCaptionsResponse = await fetchYouTubeCaption({ segment_id: segmentId })

      // Discard stale responses from superseded segment requests.
      if (requestCounterRef.current !== requestId) return

      if (response.status === 'failed') {
        setState({
          status: 'error',
          message: response.message ?? 'Caption service is unavailable. Please try again.',
        })
        return
      }

      // success with no captions available
      if (!response.caption_available) {
        setState({ status: 'no-captions' })
        return
      }

      const freshCaptionState = toSuccessState(response)
      setState(freshCaptionState)
      // Warm the pre-fetch cache so navigating away and back to this segment is instant.
      prefetchCacheRef.current.set(segmentId, freshCaptionState)
    } catch (err) {
      if (requestCounterRef.current !== requestId) return
      const resp = (err as { response?: { status?: number; data?: { error_code?: string } } })?.response
      if (resp?.status === 503) {
        if (resp.data?.error_code === 'YOUTUBE_API_UNAVAILABLE') {
          setState({ status: 'api-unavailable' })
        } else {
          // SUBSCRIPTION_QUOTA_EXCEEDED or unknown 503
          setState({ status: 'quota-exceeded' })
        }
      } else {
        setState({
          status: 'error',
          message: 'Failed to fetch captions. Please try again.',
        })
      }
    }
  }, [])

  /**
   * Fetch only DB-cached transcript for the segment (cache_only=true).
   * yt-dlp is never invoked. On cache miss, stays idle so the "Get Transcript"
   * button remains visible and enabled. Call this automatically on segment select.
   *
   * @param options.force - When true, bypass the warm prefetch cache and make a
   *   fresh DB call. Use this after a user save so the updated transcript is shown
   *   immediately without a page reload. The cache is also evicted so concurrent
   *   callers don't get stale data; after the fresh fetch succeeds the new result
   *   is written back to the cache for future non-forced calls.
   */
  const fetchFromCache = useCallback(async (segmentId: number, options?: { force?: boolean }) => {
    const requestId = ++requestCounterRef.current

    if (!options?.force) {
      // Check warm pre-fetch cache first: if we already have the result, apply
      // it synchronously without going through the loading state.
      const warmed = prefetchCacheRef.current.get(segmentId)
      if (warmed && warmed.status !== 'loading') {
        setState(warmed)
        return
      }
    } else {
      // Force refresh: evict stale entry so concurrent callers also get fresh data.
      prefetchCacheRef.current.delete(segmentId)
    }

    setState({ status: 'loading' })

    try {
      const response: YouTubeCaptionsResponse = await fetchYouTubeCaption({
        segment_id: segmentId,
        cache_only: true,
      })

      // Discard stale responses from superseded segment requests.
      if (requestCounterRef.current !== requestId) return

      if (response.status === 'failed') {
        // On a cache-only call, treat failures as idle rather than error — the
        // user can still manually trigger a full fetch with the button.
        setState({ status: 'idle' })
        return
      }

      // Cache miss: caption_available=false with no source means nothing is cached.
      // Stay idle so the "Get Transcript" button is shown.
      if (!response.caption_available) {
        setState({ status: 'idle' })
        return
      }

      const freshState = toSuccessState(response)
      setState(freshState)
      // Update warm cache with fresh result so subsequent auto-selects are instant.
      prefetchCacheRef.current.set(segmentId, freshState)
    } catch {
      if (requestCounterRef.current !== requestId) return
      // Network error on cache-only: silently stay idle.
      setState({ status: 'idle' })
    }
  }, [])

  /** Reset to idle — call this when the selected segment changes. */
  const reset = useCallback(() => {
    setState({ status: 'idle' })
  }, [])

  /**
   * Remove a segment from the warm prefetch cache.
   * Call this after a user transcript save so the next fetchFromCache call for
   * that segment goes to the DB instead of returning stale pre-save data.
   */
  const evictFromCache = useCallback((segmentId: number) => {
    prefetchCacheRef.current.delete(segmentId)
  }, [])

  return { state, fetchCaption, fetchFromCache, prefetch, reset, evictFromCache }
}
